// One fetch wrapper for the whole app.
// - cookie session (same-origin), CSRF header on every mutating request
// - 401 -> the registered handler sends the user to /login
// - 4xx/5xx -> ApiError carrying the server's human-written `detail`

export const CSRF_HEADER = 'X-Requested-With';
export const CSRF_VALUE = 'company-workspace';

export class ApiError extends Error {
  status: number;
  detail: string;
  constructor(status: number, detail: string) {
    super(detail);
    this.status = status;
    this.detail = detail;
  }
}

let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null) {
  onUnauthorized = fn;
}

type Detail = string | { msg?: string; loc?: (string | number)[] }[] | undefined;

function detailText(detail: Detail, status: number): string {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        const where = (d.loc ?? []).filter((x) => x !== 'body').join('.');
        return where ? `${where}: ${d.msg ?? 'invalid'}` : d.msg ?? 'invalid';
      })
      .join('; ');
  }
  if ([413, 502, 503, 504].includes(status)) {
    return (
      `the server's proxy refused or dropped the request (HTTP ${status}); ` +
      'a file or report this large may be over the upload limit'
    );
  }
  return `request failed (HTTP ${status})`;
}

export function newIdempotencyKey(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const b = new Uint8Array(16);
  c.getRandomValues(b);
  return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
}

export type Query = Record<string, string | number | boolean | null | undefined | (string | number)[]>;

export function qs(params: Query | URLSearchParams | undefined): string {
  if (!params) return '';
  const sp = params instanceof URLSearchParams ? params : new URLSearchParams();
  if (!(params instanceof URLSearchParams)) {
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === null || v === '') continue;
      if (Array.isArray(v)) v.forEach((x) => sp.append(k, String(x)));
      else sp.append(k, String(v));
    }
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

interface Options {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  form?: FormData;
  /** raw bytes sent as application/octet-stream (chunked uploads) */
  raw?: Blob;
  idempotencyKey?: string;
  signal?: AbortSignal;
  /** do not redirect on 401 (the login form itself) */
  noRedirect?: boolean;
}

export async function api<T = unknown>(path: string, opts: Options = {}): Promise<T> {
  const method = opts.method ?? (opts.body !== undefined || opts.form || opts.raw ? 'POST' : 'GET');
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (method !== 'GET') headers[CSRF_HEADER] = CSRF_VALUE;
  if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
  let body: BodyInit | undefined;
  if (opts.form) body = opts.form;
  else if (opts.raw) {
    headers['Content-Type'] = 'application/octet-stream';
    body = opts.raw;
  }
  else if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }
  let res: Response;
  try {
    res = await fetch(path, { method, headers, body, credentials: 'same-origin', signal: opts.signal });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new ApiError(0, 'the server could not be reached; check that the API is running');
  }
  if (res.status === 401 && !opts.noRedirect) {
    onUnauthorized?.();
    throw new ApiError(401, 'your session has ended; sign in again');
  }
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }
  if (!res.ok) {
    const detail = data && typeof data === 'object' ? (data as { detail?: Detail }).detail : undefined;
    throw new ApiError(res.status, detailText(detail ?? (typeof data === 'string' ? data : undefined), res.status));
  }
  return data as T;
}

export function errorText(e: unknown): string {
  if (e instanceof ApiError) {
    return e.detail;
  }
  return e instanceof Error ? e.message : String(e);
}
